import { supabase } from './supabase';
import { getUtcToday, getUtcYesterday } from './local-date';
import { planIncludes, type PlanSlug } from './subscriptions';
import i18n from '../i18n';

export const CREDIT_REWARDS = {
  stamp_claim: 15,
  weekly_claim: 25,
  trip_created: 20,
  trip_completed: 50,
  badge_earned: 30,
  community_update_submitted: 10,
  community_update_verified: 25,
  community_vote_cast: 5,
  daily_check_in: 10,
  streak_bonus_7: 50,
  streak_bonus_30: 200,
  referral_bonus: 100,
  level_up: 40,
} as const;

export function getDailyCheckInReward(streakDay: number): number {
  return CREDIT_REWARDS.daily_check_in + Math.min(streakDay - 1, 6) * 2;
}

export interface UserCredits {
  user_id: string;
  balance: number;
  lifetime_earned: number;
  lifetime_spent: number;
  daily_streak: number;
  longest_daily_streak: number;
  last_check_in: string | null;
  referral_code: string | null;
}

export interface CreditTransaction {
  id: string;
  user_id: string;
  amount: number;
  type: 'earn' | 'spend';
  source: string;
  description: string | null;
  reference_id: string | null;
  created_at: string;
}

export interface ShopItem {
  id: string;
  slug: string;
  name: string;
  description: string;
  icon_emoji: string;
  category: 'theme' | 'frame' | 'title' | 'feature' | 'experience';
  price: number;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  active: boolean;
  price_usd: number | null;
  stripe_price_id: string | null;
  collection_type: 'standard' | 'subscription_included' | 'premium_drop';
}

export interface LeaderboardEntry {
  rank: number;
  user_id: string;
  display_name: string;
  lifetime_earned: number;
  balance: number;
}

function generateReferralCode(): string {
  const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const digits = '0123456789';
  let code = '';
  for (let i = 0; i < 3; i++) code += letters[Math.floor(Math.random() * letters.length)];
  for (let i = 0; i < 4; i++) code += digits[Math.floor(Math.random() * digits.length)];
  return code;
}

export async function ensureUserCredits(userId: string): Promise<UserCredits> {
  const { data: existing } = await supabase
    .from('user_credits')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (existing) {
    return {
      user_id: existing.user_id,
      balance: existing.balance,
      lifetime_earned: existing.lifetime_earned,
      lifetime_spent: existing.lifetime_spent,
      daily_streak: existing.daily_streak,
      longest_daily_streak: existing.longest_daily_streak,
      last_check_in: existing.last_check_in,
      referral_code: existing.referral_code,
    };
  }

  const code = generateReferralCode();
  const { data: created } = await supabase
    .from('user_credits')
    .upsert(
      { user_id: userId, referral_code: code },
      { onConflict: 'user_id', ignoreDuplicates: true }
    )
    .select()
    .maybeSingle();

  return {
    user_id: userId,
    balance: created?.balance ?? 0,
    lifetime_earned: created?.lifetime_earned ?? 0,
    lifetime_spent: created?.lifetime_spent ?? 0,
    daily_streak: created?.daily_streak ?? 0,
    longest_daily_streak: created?.longest_daily_streak ?? 0,
    last_check_in: created?.last_check_in ?? null,
    referral_code: created?.referral_code ?? code,
  };
}

export async function earnCredits(params: {
  userId: string;
  amount: number;
  source: string;
  description?: string;
  referenceId?: string;
}): Promise<void> {
  const { userId, amount, source, description, referenceId } = params;
  if (amount <= 0) return;

  await ensureUserCredits(userId);

  await supabase.from('credit_transactions').insert({
    user_id: userId,
    amount,
    type: 'earn',
    source,
    description: description || null,
    reference_id: referenceId || null,
  });

  const { data: current } = await supabase
    .from('user_credits')
    .select('balance, lifetime_earned')
    .eq('user_id', userId)
    .maybeSingle();

  await supabase
    .from('user_credits')
    .update({
      balance: (current?.balance ?? 0) + amount,
      lifetime_earned: (current?.lifetime_earned ?? 0) + amount,
      updated_at: new Date().toISOString(),
    })
    .eq('user_id', userId);

  if (typeof window !== 'undefined') {
    import('../components/credits/CreditsEarnToast').then(m => {
      m.showCreditToast(amount, source);
    }).catch(() => {});
  }
}

export async function spendCredits(params: {
  userId: string;
  amount: number;
  source: string;
  description?: string;
  referenceId?: string;
}): Promise<{ success: boolean; error?: string }> {
  const { userId, amount, source, description, referenceId } = params;

  const credits = await ensureUserCredits(userId);
  if (credits.balance < amount) {
    return { success: false, error: 'Insufficient credits' };
  }

  await supabase.from('credit_transactions').insert({
    user_id: userId,
    amount,
    type: 'spend',
    source,
    description: description || null,
    reference_id: referenceId || null,
  });

  await supabase
    .from('user_credits')
    .update({
      balance: credits.balance - amount,
      lifetime_spent: credits.lifetime_spent + amount,
      updated_at: new Date().toISOString(),
    })
    .eq('user_id', userId);

  return { success: true };
}

export async function performDailyCheckIn(userId: string): Promise<{
  success: boolean;
  creditsEarned: number;
  streakDay: number;
  streakBonus?: number;
  error?: string;
}> {
  const today = getUtcToday();

  const { data: existing } = await supabase
    .from('daily_check_ins')
    .select('id')
    .eq('user_id', userId)
    .eq('check_in_date', today)
    .maybeSingle();

  if (existing) {
    return { success: false, creditsEarned: 0, streakDay: 0, error: 'Already checked in today' };
  }

  const credits = await ensureUserCredits(userId);

  const yesterdayStr = getUtcYesterday();

  let streakDay: number;
  if (credits.last_check_in === yesterdayStr) {
    streakDay = credits.daily_streak + 1;
  } else {
    streakDay = 1;
  }

  const reward = getDailyCheckInReward(streakDay);

  await supabase.from('daily_check_ins').insert({
    user_id: userId,
    check_in_date: today,
    credits_earned: reward,
    streak_day: streakDay,
  });

  await earnCredits({
    userId,
    amount: reward,
    source: 'daily_check_in',
    description: `Day ${streakDay} check-in`,
  });

  let streakBonus: number | undefined;
  if (streakDay === 7) {
    streakBonus = CREDIT_REWARDS.streak_bonus_7;
    await earnCredits({
      userId,
      amount: streakBonus,
      source: 'streak_bonus',
      description: '7-day streak bonus',
    });
  } else if (streakDay === 30) {
    streakBonus = CREDIT_REWARDS.streak_bonus_30;
    await earnCredits({
      userId,
      amount: streakBonus,
      source: 'streak_bonus',
      description: '30-day streak bonus',
    });
  }

  const newLongest = Math.max(credits.longest_daily_streak, streakDay);
  await supabase
    .from('user_credits')
    .update({
      daily_streak: streakDay,
      longest_daily_streak: newLongest,
      last_check_in: today,
      updated_at: new Date().toISOString(),
    })
    .eq('user_id', userId);

  return { success: true, creditsEarned: reward, streakDay, streakBonus };
}

export async function claimReferralBonus(
  referrerCode: string,
  newUserId: string
): Promise<{ success: boolean; error?: string }> {
  const { data: referrer } = await supabase
    .from('user_credits')
    .select('user_id')
    .eq('referral_code', referrerCode.toUpperCase())
    .maybeSingle();

  if (!referrer) return { success: false, error: i18n.t('pages:referral.invalidCode', 'Invalid referral code') };
  if (referrer.user_id === newUserId) return { success: false, error: i18n.t('pages:referral.cannotSelf', 'Cannot refer yourself') };

  const { error: insertErr } = await supabase.from('referral_claims').insert({
    referrer_user_id: referrer.user_id,
    referred_user_id: newUserId,
    credits_awarded: CREDIT_REWARDS.referral_bonus,
  });

  if (insertErr) {
    if (insertErr.code === '23505') return { success: false, error: i18n.t('pages:referral.alreadyClaimed', 'Referral already claimed') };
    return { success: false, error: i18n.t('pages:referral.failed', 'Failed to process referral') };
  }

  await Promise.all([
    earnCredits({
      userId: referrer.user_id,
      amount: CREDIT_REWARDS.referral_bonus,
      source: 'referral_bonus',
      description: 'Referral bonus (referrer)',
    }),
    earnCredits({
      userId: newUserId,
      amount: CREDIT_REWARDS.referral_bonus,
      source: 'referral_bonus',
      description: 'Referral bonus (new user)',
    }),
  ]);

  return { success: true };
}

export async function fetchCreditHistory(
  userId: string,
  limit = 20
): Promise<CreditTransaction[]> {
  const { data, error } = await supabase
    .from('credit_transactions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error || !data) return [];
  return data as CreditTransaction[];
}

export async function fetchLeaderboard(limit = 20): Promise<LeaderboardEntry[]> {
  const { data, error } = await supabase
    .from('user_credits')
    .select('user_id, lifetime_earned, balance')
    .order('lifetime_earned', { ascending: false })
    .limit(limit);

  if (error || !data) return [];

  return data.map((row, idx) => ({
    rank: idx + 1,
    user_id: row.user_id,
    display_name: `Traveler ${row.user_id.substring(0, 4).toUpperCase()}`,
    lifetime_earned: row.lifetime_earned,
    balance: row.balance,
  }));
}

export async function fetchLeaderboardByPeriod(
  period: 'week' | 'month' | 'all',
  limit = 20
): Promise<LeaderboardEntry[]> {
  if (period === 'all') return fetchLeaderboard(limit);

  const now = new Date();
  const since = period === 'week'
    ? new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7).toISOString()
    : new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

  const { data, error } = await supabase
    .from('credit_transactions')
    .select('user_id, amount')
    .eq('type', 'earn')
    .gte('created_at', since);

  if (error || !data) return [];

  const totals = new Map<string, number>();
  data.forEach(row => {
    totals.set(row.user_id, (totals.get(row.user_id) || 0) + row.amount);
  });

  return Array.from(totals.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([user_id, earned], idx) => ({
      rank: idx + 1,
      user_id,
      display_name: `Traveler ${user_id.substring(0, 4).toUpperCase()}`,
      lifetime_earned: earned,
      balance: 0,
    }));
}

export async function fetchShopItems(includeInactive = false): Promise<ShopItem[]> {
  let query = supabase.from('shop_items').select('*').order('sort_order', { ascending: true });
  if (!includeInactive) {
    query = query.eq('active', true);
  }
  const { data, error } = await query;
  if (error || !data) return [];
  return data as ShopItem[];
}

export async function purchaseItem(
  userId: string,
  itemId: string
): Promise<{ success: boolean; error?: string }> {
  const { data: existing } = await supabase
    .from('user_purchases')
    .select('id')
    .eq('user_id', userId)
    .eq('item_id', itemId)
    .maybeSingle();

  if (existing) return { success: false, error: 'Already owned' };

  const { data: item } = await supabase
    .from('shop_items')
    .select('price, name')
    .eq('id', itemId)
    .maybeSingle();

  if (!item) return { success: false, error: 'Item not found' };

  const result = await spendCredits({
    userId,
    amount: item.price,
    source: 'shop_purchase',
    description: `Purchased ${item.name}`,
    referenceId: itemId,
  });

  if (!result.success) return result;

  const { error: purchaseError } = await supabase.from('user_purchases').insert({
    user_id: userId,
    item_id: itemId,
    credits_spent: item.price,
  });

  if (purchaseError) return { success: false, error: 'Failed to record purchase' };

  invalidateFeatureCache();
  return { success: true };
}

export async function fetchUserPurchases(userId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('user_purchases')
    .select('item_id')
    .eq('user_id', userId);

  if (error || !data) return [];
  return data.map(p => p.item_id);
}

// Base themes are mutually exclusive — only one can be the active visual
// theme. Overlays are combinable — any subset can be active alongside a
// base theme (or alongside Standard/null).
const BASE_THEMES = new Set(['exp-manga', 'exp-ukiyo-e', 'exp-manga-v3']);
const OVERLAYS = new Set(['exp-sensei', 'exp-seasons', 'exp-explorer']);

export function isBaseTheme(slug: string): boolean {
  return BASE_THEMES.has(slug);
}

export function isOverlay(slug: string): boolean {
  return OVERLAYS.has(slug);
}

export async function fetchActiveCosmetics(userId: string): Promise<{
  activeFrame: string | null;
  activeTitle: string | null;
  activeExperience: string | null;
  activeLightTheme: string | null;
  activeDarkTheme: string | null;
  activeOverlays: string[];
}> {
  const { data } = await supabase
    .from('user_credits')
    .select('active_frame, active_title, active_experience, active_light_theme, active_dark_theme, active_overlays')
    .eq('user_id', userId)
    .maybeSingle();
  return {
    activeFrame: data?.active_frame || null,
    activeTitle: data?.active_title || null,
    activeExperience: data?.active_experience || null,
    activeLightTheme: data?.active_light_theme || null,
    activeDarkTheme: data?.active_dark_theme || null,
    activeOverlays: (data?.active_overlays as string[] | null) || [],
  };
}

export async function equipItem(userId: string, slug: string, category: 'frame' | 'title' | 'experience' | 'theme'): Promise<void> {
  if (category === 'theme') {
    const { error } = await supabase
      .from('user_credits')
      .update({ active_light_theme: slug, active_dark_theme: slug })
      .eq('user_id', userId);
    if (error) throw error;
    return;
  }

  if (category === 'experience') {
    if (isBaseTheme(slug)) {
      // Base themes are mutually exclusive — overwrite active_experience.
      // Active overlays are intentionally preserved.
      const { error } = await supabase
        .from('user_credits')
        .update({ active_experience: slug })
        .eq('user_id', userId);
      if (error) throw error;
      return;
    }
    if (isOverlay(slug)) {
      // Overlays are toggled in the array — read-modify-write to add.
      const { data } = await supabase
        .from('user_credits')
        .select('active_overlays')
        .eq('user_id', userId)
        .maybeSingle();
      const current: string[] = (data?.active_overlays as string[] | null) || [];
      if (!current.includes(slug)) {
        const { error } = await supabase
          .from('user_credits')
          .update({ active_overlays: [...current, slug] })
          .eq('user_id', userId);
        if (error) throw error;
      }
      return;
    }
    // Unknown experience slug — fall through and treat like a base for safety.
    const { error } = await supabase
      .from('user_credits')
      .update({ active_experience: slug })
      .eq('user_id', userId);
    if (error) throw error;
    return;
  }

  const field = category === 'frame' ? 'active_frame' : 'active_title';
  const { error } = await supabase
    .from('user_credits')
    .update({ [field]: slug })
    .eq('user_id', userId);
  if (error) throw error;
}

export async function unequipItem(userId: string, category: 'frame' | 'title' | 'experience' | 'theme', slug?: string): Promise<void> {
  if (category === 'theme') {
    const { error } = await supabase
      .from('user_credits')
      .update({ active_light_theme: null, active_dark_theme: null })
      .eq('user_id', userId);
    if (error) throw error;
    return;
  }

  if (category === 'experience') {
    if (slug && isOverlay(slug)) {
      // Remove just this overlay from the array.
      const { data } = await supabase
        .from('user_credits')
        .select('active_overlays')
        .eq('user_id', userId)
        .maybeSingle();
      const current: string[] = (data?.active_overlays as string[] | null) || [];
      const updated = current.filter(s => s !== slug);
      const { error } = await supabase
        .from('user_credits')
        .update({ active_overlays: updated })
        .eq('user_id', userId);
      if (error) throw error;
      return;
    }
    // No slug or a base-theme slug → revert base theme to Standard.
    // Overlays are intentionally preserved.
    const { error } = await supabase
      .from('user_credits')
      .update({ active_experience: null })
      .eq('user_id', userId);
    if (error) throw error;
    return;
  }

  const field = category === 'frame' ? 'active_frame' : 'active_title';
  const { error } = await supabase
    .from('user_credits')
    .update({ [field]: null })
    .eq('user_id', userId);
  if (error) throw error;
}

export function getFrameConfig(slug: string | null): {
  label: string;
  emoji: string;
  ringClass: string;
  badgeBg: string;
} | null {
  if (!slug) return null;
  const frames: Record<string, { label: string; emoji: string; ringClass: string; badgeBg: string }> = {
    'frame-torii': {
      label: 'Torii Gate',
      emoji: '⛩️',
      ringClass: 'ring-2 ring-red-500',
      badgeBg: 'bg-red-500',
    },
    'frame-wave': {
      label: 'Great Wave',
      emoji: '🌊',
      ringClass: 'ring-2 ring-blue-400',
      badgeBg: 'bg-blue-500',
    },
    'frame-dragon': {
      label: 'Dragon',
      emoji: '🐉',
      ringClass: 'ring-2 ring-amber-400 animate-pulse',
      badgeBg: 'bg-gradient-to-br from-amber-500 to-red-500',
    },
    'frame-imperial': {
      label: 'Imperial',
      emoji: '🏯',
      ringClass: 'ring-2 ring-purple-400 shadow-[0_0_10px_rgba(168,85,247,0.3)]',
      badgeBg: 'bg-purple-500',
    },
  };
  return frames[slug] || null;
}

export function getTitleConfig(slug: string | null): {
  label: string;
  emoji: string;
  badgeClass: string;
} | null {
  if (!slug) return null;
  const titles: Record<string, { label: string; emoji: string; badgeClass: string }> = {
    'title-traveler': {
      label: 'Seasoned Traveler',
      emoji: '🧳',
      badgeClass: 'from-slate-600 to-slate-700 text-white',
    },
    'title-sensei': {
      label: 'Travel Sensei',
      emoji: '🎓',
      badgeClass: 'from-blue-600 to-indigo-600 text-white',
    },
    'title-shogun': {
      label: 'Shogun of Routes',
      emoji: '⚔️',
      badgeClass: 'from-orange-600 to-red-600 text-white',
    },
    'title-emperor': {
      label: 'Emperor Explorer',
      emoji: '👑',
      badgeClass: 'from-amber-500 to-yellow-500 text-gray-900',
    },
  };
  return titles[slug] || null;
}

const featureCache = new Map<string, boolean>();
let featureCacheUser: string | null = null;

export async function hasFeature(userId: string, featureSlug: string): Promise<boolean> {
  const { data } = await supabase
    .from('user_purchases')
    .select('id, item:shop_items!inner(slug)')
    .eq('user_id', userId)
    .eq('shop_items.slug', featureSlug)
    .maybeSingle();
  return !!data;
}

export async function hasFeatureCached(userId: string, featureSlug: string): Promise<boolean> {
  if (featureCacheUser !== userId) {
    featureCache.clear();
    featureCacheUser = userId;
  }
  const cacheKey = `${userId}:${featureSlug}`;
  if (featureCache.has(cacheKey)) return featureCache.get(cacheKey)!;

  const { data: { user } } = await supabase.auth.getUser();
  if (user?.app_metadata?.role === 'admin') {
    featureCache.set(cacheKey, true);
    return true;
  }

  const { data: sub } = await supabase
    .from('subscriptions')
    .select('plan_slug')
    .eq('user_id', userId)
    .in('status', ['active', 'trialing'])
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (sub?.plan_slug && planIncludes(sub.plan_slug as PlanSlug, featureSlug)) {
    featureCache.set(cacheKey, true);
    return true;
  }

  const result = await hasFeature(userId, featureSlug);
  featureCache.set(cacheKey, result);
  return result;
}

export function invalidateFeatureCache() {
  featureCache.clear();
  featureCacheUser = null;
}

export async function awardCredits(userId: string, source: string): Promise<void> {
  const amount = (CREDIT_REWARDS as Record<string, number>)[source] ?? 10;
  await earnCredits({ userId, amount, source });
}
